import React, { useEffect, useState, useCallback, useMemo, memo } from "react";
import { Button, Row } from "react-bootstrap";
import "./CreateMachine.css";
import instance, { apiEncryptRequest, currencyReturn, decryptData } from "../../Api";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../AuthContext";
import Loader from "../common/Loader";
import toast, { Toaster } from "react-hot-toast";
import AppToast from "../../AppToast";
import { FaX } from "react-icons/fa6";
import CouponBox from "../common/CouponBox";

// ─── Constants ─────────────────────────────────────────────────────────────────
const INNER_BUTTONS  = ["Standard", "CPU Proactive", "RAM Proactive"];
const NATIVE_PAGES   = ["Standard", "CPU Proactive", "RAM Proactive", "Custom Configure"];
const DISK_LABELS    = { hdd: "HDD Disk", ssd: "SSD Disk", nvme: "NVMe Disk" };



 

const getCusConfig = (configType, diskType, { stdCusHDD, stdCusSSD, stdCusNVM, cpuCusHDD, cpuCusSSD, cpuCusNVM, ramCusHDD, ramCusSSD, ramCusNVM }) => {
  if (configType === 1) return diskType === "ssd" ? stdCusSSD : diskType === "nvme" ? stdCusNVM : stdCusHDD;
  if (configType === 2) return diskType === "ssd" ? cpuCusSSD : diskType === "nvme" ? cpuCusNVM : cpuCusHDD;
  return                       diskType === "ssd" ? ramCusSSD : diskType === "nvme" ? ramCusNVM : ramCusHDD;
};

const getDiskPriceKey = (diskType) =>
  diskType === "ssd" ? "ssd_price" : diskType === "nvme" ? "nvme_price" : "hdd_rate";

const getStocks = (activeButton, diskType, stocks) => {
  const prefix = activeButton === "Standard" ? "s" : activeButton === "CPU Proactive" ? "c" : "r";
  const suffix = diskType === "ssd" ? "Ssd" : diskType === "nvme" ? "Nvm" : "Hdd";
  return stocks[`${prefix}${suffix}Stocks`];
};

const getVmList = (activePage, diskType, lists) => {
  const { sSsd, sNvm, sHdd, cSsd, cNvm, cHdd, rSsd, rNvm, rHdd } = lists;
  if (activePage === "Standard")      return diskType === "ssd" ? sSsd : diskType === "nvme" ? sNvm : sHdd;
  if (activePage === "CPU Proactive") return diskType === "ssd" ? cSsd : diskType === "nvme" ? cNvm : cHdd;
  return                                     diskType === "ssd" ? rSsd : diskType === "nvme" ? rNvm : rHdd;
};


const getItemPrice = (item, diskType) => {
  const rate = diskType === "ssd" ? item.ssd_price : diskType === "nvme" ? item.nvme_price : item.hdd_rate;
  return rate * item.hdd + item.cpu_rate * item.cpu + item.ram * item.ram_rate;
};

const showToast = (msg, isMobile) =>
  toast((t) => <AppToast id={t.id} message={msg} isMobile={isMobile} />);

// ─── Responsive CSS ────────────────────────────────────────────────────────────
const RESPONSIVE_CSS = `
  /* OS selector row: horizontal scroll on mobile */
  .scrollable-container {
    display: flex !important;
    flex-direction: row !important;
    overflow-x: auto !important;
    -webkit-overflow-scrolling: touch !important;
    scrollbar-width: none !important;
    width: 100% !important;
    box-sizing: border-box !important;
    padding-bottom: 8px !important;
  }
  .scrollable-container::-webkit-scrollbar { display: none; }

  /* Page tabs: flex scroll row, no fixed offsets */
  .cm-page-tabs {
    display: flex !important;
    overflow-x: auto !important;
    -webkit-overflow-scrolling: touch !important;
    scrollbar-width: none !important;
    gap: 4px !important;
    width: 100% !important;
    box-sizing: border-box !important;
    margin-bottom: 8px !important;
  }
  .cm-page-tabs::-webkit-scrollbar { display: none; }
  .cm-page-tabs > div { flex-shrink: 0 !important; margin-left: 0 !important; }

  /* Controls bar: wrap on small screens */
  .cm-controls {
    display: flex !important;
    flex-wrap: wrap !important;
    align-items: center !important;
    gap: 8px !important;
    width: 100% !important;
    box-sizing: border-box !important;
  }

  /* Package card: cap width to viewport */
  .tab-box-mobile-create-machine .package {
    width: calc(100vw - 24px) !important;
    max-width: 400px !important;
    margin: 0 auto 20px auto !important;
    box-sizing: border-box !important;
  }
  .tab-box-mobile-create-machine .custom-checkbox span {
    width: calc(100vw - 72px) !important;
    max-width: 380px !important;
    box-sizing: border-box !important;
    margin-left: 12px !important;
  }

  /* Vertical sliders: distribute across screen */
  .cm-vrt-sliders {
    position: relative !important;
    display: flex !important;
    flex-direction: row !important;
    justify-content: space-around !important;
    align-items: flex-start !important;
    width: 100% !important;
    margin: 16px 0 !important;
    padding: 0 8px !important;
    box-sizing: border-box !important;
    min-height: 260px !important;
  }
  .cm-vrt-sliders .range-slider-vrt {
    position: relative !important;
    flex: 1 !important;
    display: flex !important;
    flex-direction: column !important;
    align-items: center !important;
    gap: 8px !important;
  }
  .cm-vrt-sliders .custom-rangeInput-vrt {
    position: relative !important;
    left: 0 !important;
    writing-mode: vertical-lr !important;
    direction: rtl !important;
    width: 8px !important;
    height: 200px !important;
    cursor: pointer !important;
  }
  .cm-vrt-sliders .tooltip-horz-vrt {
    position: relative !important;
    left: 0 !important;
    top: 0 !important;
    text-align: center !important;
    font-size: 12px !important;
    font-weight: 700 !important;
    color: #035189 !important;
  }
`;

// ─── DiskSelect ────────────────────────────────────────────────────────────────
// stocksByType = { hdd: [...], ssd: [...], nvme: [...] }
// item.hdd is the disk GB for ALL disk types (API uses hdd as the disk size field)
const DiskSelect = memo(({ diskType, stocksByType, item, onChangeDisk }) => (
  <select
    name="plan_time"
    value={diskType}
    style={{ borderRadius: "30px", padding: "5px", border: "2px solid #e97730" }}
    onChange={(e) => onChangeDisk(e.target.value)}
  >
    {stocksByType?.hdd?.[0]?.stocks == 1 && (
      <option value="hdd">{item?.hdd} GB HDD Disk</option>
    )}
    {stocksByType?.ssd?.[0]?.stocks == 1 && (
      <option value="ssd">{item?.hdd} GB SSD Disk</option>
    )}
    {stocksByType?.nvme?.[0]?.stocks == 1 && (
      <option value="nvme">{item?.hdd} GB NVMe Disk</option>
    )}
  </select>
));

// ─── PackageCard ───────────────────────────────────────────────────────────────
// Fix: on mobile the specs box was rendered BEFORE the label/price span,
// causing the upside-down layout. Correct order: label/price span → specs box.
const PackageCard = memo(({
  item, idx, selectedIdx, activePage, diskType, isMobile,
  smuser, appCurrency, newMachineTime,
  hoverIndex, handleMouseEnter, handleMouseLeave,
  stocksByType, onChangeDisk, onSelect,
}) => {
  const price      = getItemPrice(item, diskType);
  const isSelected = selectedIdx === idx;
  const dayPrice   = (price / (appCurrency || 1) / 30).toFixed(2);

  return (
    <div className="package" style={{ marginBottom: isMobile ? "20px" : 0 }}>
      {/* ── 1. Title + price card (top) ── */}
      <label className="custom-checkbox" onClick={() => onSelect(item, idx, price)}>
        <span style={{
          marginTop:   isMobile ? "10px" : "35px",
          marginLeft:  isMobile ? "12px" : "27px",
          padding:     "15px",
          borderRadius:"35px",
          display:     "inline-block",
          width:       isMobile ? "calc(100% - 24px)" : "100%",
          fontSize:    isMobile ? "17px" : "22px",
          background:  isSelected ? "#035189" : "linear-gradient(180deg, #bfbfbf, #e5e5e5 50%)",
          color:       "#000",
          cursor:      "pointer",
          userSelect:  "none",
          fontWeight:  "bold",
          height:      isMobile ? "10rem" : "12rem",
          boxShadow:   "2px 2px 0px rgba(0,0,0,0.2)",
          boxSizing:   "border-box",
        }}>
          <div className={`top-head see-white-text${isMobile ? "" : " top-head-sub-style"}`}>
            <div className="media" onMouseEnter={() => handleMouseEnter(idx)} onMouseLeave={handleMouseLeave}>
              <img className="normal"    src="/images/admin/02-VM/title-bg-orange.svg" alt="" style={{ width: isMobile ? "100%" : "20rem" }} />
              <img className="hover-img" src="/images/admin/02-VM/title-bg-white.svg"  alt="" style={{ width: isMobile ? "100%" : "20rem" }} />
              <h4 className={isMobile ? undefined : "login-text"} style={{
                marginTop: isMobile ? "-40px" : undefined,
                fontSize:  isMobile ? "24px" : "31px",
                color:     hoverIndex === idx ? "rgb(3,81,137)" : "white",
              }}>
                {activePage}
              </h4>
            </div>
          </div>
          {!isMobile && <div className="log-in"><a className="media-link" /></div>}
          <div className="top-body theme-color-blue">
            <div className="price" style={{ color: isSelected ? "#fff" : "#545454", textAlign: "center", marginTop: "15px" }}>
              {smuser && appCurrency && currencyReturn({ price, symbol: smuser.prefer_currency, rates: appCurrency })}
              <span> Month </span>
            </div>
            <div className="price" style={{ color: isSelected ? "#fff" : "#545454", textAlign: "center" }}>
              {dayPrice} / <span>day</span>
            </div>
          </div>
        </span>
      </label>

      {/* ── 2. Specs box (below title card) ── */}
      <div style={{
        marginLeft: isMobile ? "12px" : "45px",
        position:   isMobile ? undefined : "relative",
        left:       isMobile ? undefined : "1rem",
        marginTop:  "5px",
      }}>
        <div className={isMobile ? undefined : "media"}>
          <img
            className={isMobile ? "gray-bg" : "normal"}
            src={isSelected ? "/images/admin/02-VM/orange-box-bg.svg" : "/images/admin/02-VM/gray-box-bg.svg"}
            alt=""
            style={{ height: isMobile ? "15rem" : "17rem", width: isMobile ? "calc(100% - 8px)" : "20rem", maxWidth: "100%" }}
          />
          {!isMobile && <img className="hover-img" src="/images/admin/02-VM/orange-box-bg.svg" alt="" style={{ height: "17rem" }} />}
        </div>
        <div style={{ marginTop: isMobile ? "-14rem" : "-16rem", marginLeft: isMobile ? "5px" : "-35px", color: "#444" }}>
          {[`${item.cpu} vCPU`, `${item.ram} GB RAM`].map((label) => (
            <div key={label} className="price" style={{ textAlign: "center", fontWeight: 600, fontSize: 24, position: "relative", marginTop: 15 }}>
              {label}
            </div>
          ))}
          <div className="ssd price" style={{ textAlign: "center", fontWeight: 600, fontSize: 24, position: "relative", marginTop: 15 }}>
            <DiskSelect diskType={diskType} stocksByType={stocksByType} item={item} onChangeDisk={onChangeDisk} />
          </div>
          <div className="data price" style={{ textAlign: "center", fontWeight: 600, fontSize: 24, position: "relative", marginTop: 15 }}>
            {item.data_transfer} TB Bandwidth
          </div>
        </div>
      </div>
    </div>
  );
});

const OsSelector = memo(({ label, icon, iconStyle, options, value, onChange, isMobile }) => (
  <div style={{ width: isMobile ? "10rem" : "15rem", padding: "20px", marginRight: isMobile ? "10px" : "15px", flexShrink: 0 }}>
    <div className="in-border" style={{ alignContent: "center", height: isMobile ? 100 : 130, width: isMobile ? 100 : 130, border: "2px solid #E97730", borderRadius: "50%", backgroundColor: "transparent", padding: 0, marginLeft: isMobile ? 30 : 50 }}>
      <div className="in-border" style={{ height: isMobile ? 80 : 110, width: isMobile ? 80 : 110, padding: "1px", border: "2px solid #E97730", borderRadius: "50%", margin: "auto", backgroundColor: "#E97730" }}>
        <figure style={{ background: "#e97730", borderRadius: "50%", padding: "20px 8px 0px 0px", objectFit: "cover", display: "table", margin: "auto" }}>
          <img src={icon} alt={label} style={iconStyle ?? { width: isMobile ? 40 : 60, height: isMobile ? 40 : 60 }} />
        </figure>
      </div>
    </div>
    <h4 style={{ textAlign: "center", fontSize: isMobile ? 14 : 24, textTransform: "capitalize", color: "#035189", fontWeight: isMobile ? 600 : 500, marginLeft: isMobile ? 24 : 0, marginTop: 5 }}>
      {label}
    </h4>
    <select
      name="plan_time"
      value={value}
      style={{ borderRadius: "30px", marginRight: "10px", padding: isMobile ? "5px" : "10px 15px", border: "2px solid #e97730", width: isMobile ? "10rem" : "15rem" }}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="Select">Select</option>
      {options?.map((item, i) => (
        <option key={i} value={Object.values(item)[0]}>{Object.keys(item)[0]}</option>
      ))}
    </select>
  </div>
));

// ─── Main Component ─────────────────────────────────────────────────────────────
const CreateMachine = ({ isMobile }) => {
  const { smuser, appCurrency, isLoginByParentUser } = useAuth();
  const { updateUserDetails, updateCurrencyRate, updateAppCurrency } = useAuth();
  const navigate = useNavigate();

  const [hoverIndex, setHoverIndex]                 = useState(null);
  const [activeButton, setActiveButton]             = useState("Standard");
  const [activePage, setActivePage]                 = useState("Standard");
  const [configType, setConfigType]                 = useState(1);
  const [diskType, setDisktype]                     = useState("ssd");
  const [selectedIdx, setSelectedIdx]               = useState(null);
  const [selectedCPU, setSelectedCPU]               = useState(0);
  const [selectedRAM, setSelectedRAM]               = useState(0);
  const [selectedSSD, setSelectedSSD]               = useState(0);
  const [selectedDT, setSelectedDT]                 = useState(0);
  const [loading, setLoading]                       = useState(true);
  const [showTermsCondition, setShowTermsCondition] = useState(false);
  const [showIPOptionPopup, setShowIPOptionPopup]   = useState(null);
  const [showPhoneVerify, setShowPhoneVerify]       = useState(false);
  const [phoneOTP, setPhoneOTP]                     = useState("");
  const [phone, setPhone]                           = useState("");
  const [phoneOtpSent, setPhoneOtpSent]             = useState(false);
  const [showJumpServerPopup, setShowJumpServerPopup] = useState(false);
  const [jumpServerDetails, setJumpServerDetails]   = useState(null);
  const [loadingJumpServer, setLoadingJumpServer]   = useState(false);
  const [isShowUpnetCreditContentPopup, ShowUpnetCreditContentPopup] = useState(false);
  const [stockAvailableStatus, setStockAvailableStatus] = useState(true);
  const [counter, setCounter]                       = useState(1);
  const [couponCode,     setCouponCode]     = useState("");
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [newMachineName, setNewMachineName]         = useState(null);
  const [newMahineOs, setNewMachineOs]              = useState(null);
  const [newMahineConfigId, setNewMachineConfigId]  = useState(null);
  const [newMachineTime, setNewMachineTime]         = useState("1");
  const [finalAmount, setFinalAmount]               = useState("0");
  const [machineAmt, setMachineAmt]                 = useState("0");
  const [discountRate, setDiscountRate]             = useState("0");
  const [surcharge, setSurcharge]                   = useState(0);
  const [iPPrice, setIPPrice]                       = useState(0);
  const [upnetPercentage, setUpnetPercentage]       = useState(null);
  const [addToWalletAmt, setAddToWalletAmt]         = useState(null);
  const [selectedLocation, setSelectedLocation]     = useState(null);
  const [serverLocaiton, setServerLocaiton]         = useState(null);
  const [platformSelected, setPlatformSelected]     = useState(null);
  const [userSystem, setUserSystem]                 = useState(null);

  const [customCPU, setCustomCPU]     = useState(0);
  const [customRAM, setCustomRAM]     = useState(0);
  const [customDISK, setCustomDISK]   = useState(0);
  const [customDATAT, setCustomDATAT] = useState(0);

  const [ubantuOS, setUbantuOS]             = useState(null);
  const [fedoraOS, setFedoraOS]             = useState(null);
  const [ubantu_machine, setUbantu_machine] = useState(null);
  const [fedora_machine, setFedora_machine] = useState(null);
  const [ubuntu_fa, setUbuntu_fa]           = useState([]);
  const [fedora_fa, setFedora_fa]           = useState([]);
  const [centOS_fa, setCentOS_fa]           = useState([]);
  const [redhat_fa, setRedhat_fa]           = useState([]);
  const [windows_fa, setWindows_fa]         = useState([]);
  const [linux_fa, setLinux_fa]             = useState([]);
  const [rocky_fa, setRocky_fa]             = useState([]);
  const [debian_fa, setDebian_fa]           = useState([]);
  const [allOsTemplete, setAllOsTemplete]   = useState(null);

  const [sSsd, setSSsd] = useState(null); const [sHdd, setSHdd] = useState(null); const [sNvm, setSNvm] = useState(null);
  const [cSsd, setCSsd] = useState(null); const [cHdd, setCHdd] = useState(null); const [cNvm, setCNvm] = useState(null);
  const [rSsd, setRSsd] = useState(null); const [rHdd, setRHdd] = useState(null); const [rNvm, setRNvm] = useState(null);

  const [sHddStocks, setSHddStocks] = useState(null); const [sSsdStocks, setSSsdStocks] = useState(null); const [sNvmStocks, setSNvmStocks] = useState(null);
  const [cHddStocks, setCHddStocks] = useState(null); const [cSsdStocks, setCSsdStocks] = useState(null); const [cNvmStocks, setCNvmStocks] = useState(null);
  const [rHddStocks, setRHddStocks] = useState(null); const [rSsdStocks, setRSsdStocks] = useState(null); const [rNvmStocks, setRNvmStocks] = useState(null);

  const [standardRates, setStandardRates]         = useState({});
  const [cpuIntensiveRates, setCpuIntensiveRates] = useState(null);
  const [ramIntensiveRates, setRamIntensiveRates] = useState(null);

  const [stdCusHDD, setStdCusHDD] = useState({}); const [stdCusSSD, setStdCusSSD] = useState({}); const [stdCusNVM, setStdCusNVM] = useState({});
  const [cpuCusHDD, setCpuCusHDD] = useState({}); const [cpuCusSSD, setCpuCusSSD] = useState({}); const [cpuCusNVM, setCpuCusNVM] = useState({});
  const [ramCusHDD, setRamCusHDD] = useState({}); const [ramCusSSD, setRamCusSSD] = useState({}); const [ramCusNVM, setRamCusNVM] = useState({});

  const [custConfigData, setCustConfigData]         = useState({});
  const [custConfigStdHDD, setCustConfigStdHDD]     = useState({});
  const [custConfigStdSSD, setCustConfigStdSSD]     = useState({});
  const [custConfigStdNVME, setCustConfigStdNVME]   = useState({});
  const [custConfigCpuHDD, setCustConfigCpuHDD]     = useState({});
  const [custConfigCpuSSD, setCustConfigCpuSSD]     = useState({});
  const [custConfigCpuNVME, setCustConfigCpuNVME]   = useState({});
  const [custConfigRamHDD, setCustConfigRamHDD]     = useState({});
  const [custConfigRamSSD, setCustConfigRamSSD]     = useState({});
  const [custConfigRamNVME, setCustConfigRamNVME]   = useState({});

  const totalIPPrice = counter * iPPrice;
  const selectedNoIP = counter;

  const cusConfigs = useMemo(() => ({ stdCusHDD, stdCusSSD, stdCusNVM, cpuCusHDD, cpuCusSSD, cpuCusNVM, ramCusHDD, ramCusSSD, ramCusNVM }),
    [stdCusHDD, stdCusSSD, stdCusNVM, cpuCusHDD, cpuCusSSD, cpuCusNVM, ramCusHDD, ramCusSSD, ramCusNVM]);

  const activeCusConfig = useMemo(() => getCusConfig(configType, diskType, cusConfigs), [configType, diskType, cusConfigs]);

  const configMap = useMemo(() => ({
    "Standard-ssd": custConfigStdSSD, "Standard-hdd": custConfigStdHDD, "Standard-nvme": custConfigStdNVME,
    "CPU Proactive-ssd": custConfigCpuSSD, "CPU Proactive-hdd": custConfigCpuHDD, "CPU Proactive-nvme": custConfigCpuNVME,
    "RAM Proactive-ssd": custConfigRamSSD, "RAM Proactive-hdd": custConfigRamHDD, "RAM Proactive-nvme": custConfigRamNVME,
  }), [custConfigStdSSD, custConfigStdHDD, custConfigStdNVME, custConfigCpuSSD, custConfigCpuHDD, custConfigCpuNVME, custConfigRamSSD, custConfigRamHDD, custConfigRamNVME]);

  const activeConfig   = useMemo(() => configMap[`${activeButton}-${diskType}`] ?? [], [configMap, activeButton, diskType]);
  const maxVcpu        = activeConfig[0]?.vcpu ?? 0;
  const maxRam         = activeConfig[0]?.ram  ?? 0;
  const maxDisk        = activeConfig[0]?.[diskType === "ssd" ? "ssd" : diskType === "nvme" ? "nvme" : "hdd"] ?? 0;
  const maxBandwidth   = activeConfig[0]?.data_transfer ?? 0;
  const pctVcpu        = maxVcpu      ? (customCPU   * 100) / maxVcpu      : 0;
  const pctRam         = maxRam       ? (customRAM   * 100) / maxRam       : 0;
  const pctDisk        = maxDisk      ? (customDISK  * 100) / maxDisk      : 0;
  const pctBandwidth   = maxBandwidth ? (customDATAT * 100) / maxBandwidth : 0;

  const vmLists = useMemo(() => ({ sSsd, sNvm, sHdd, cSsd, cNvm, cHdd, rSsd, rNvm, rHdd }),
    [sSsd, sNvm, sHdd, cSsd, cNvm, cHdd, rSsd, rNvm, rHdd]);

  const currentVmList = useMemo(() => getVmList(activePage, diskType, vmLists) ?? [], [activePage, diskType, vmLists]);

  

  const allStocks = useMemo(() => ({ sHddStocks, sSsdStocks, sNvmStocks, cHddStocks, cSsdStocks, cNvmStocks, rHddStocks, rSsdStocks, rNvmStocks }),
    [sHddStocks, sSsdStocks, sNvmStocks, cHddStocks, cSsdStocks, cNvmStocks, rHddStocks, rSsdStocks, rNvmStocks]);

  const currentStocks = useMemo(() => getStocks(activePage === "Custom Configure" ? activeButton : activePage, diskType, allStocks),
      [activePage, activeButton, diskType, allStocks]);
  // All 3 disk-type stocks for the current page — passed as stocksByType to PackageCard/DiskSelect
  const stocksByType = useMemo(() => {
    const pg = activePage === "Custom Configure" ? activeButton : activePage;
    const prefix = pg === "Standard" ? "s" : pg === "CPU Proactive" ? "c" : "r";
    return {
      hdd:  allStocks[`${prefix}HddStocks`],
      ssd:  allStocks[`${prefix}SsdStocks`],
      nvme: allStocks[`${prefix}NvmStocks`],
    };
  }, [activePage, activeButton, allStocks]);

  const calculateCustomPrice = useCallback((config, priceKey, discRate, timeMultiplier) => {
    if (!config) return 0;
    const base = customCPU * config.cpu_rate + customRAM * config.ram_rate + customDISK * (config[priceKey] ?? 0) + customDATAT * 1;
    const discounted = base - (base * discRate) / 100;
    return smuser?.country === "India"
      ? discounted + totalIPPrice * timeMultiplier
      : discounted * (1 + surcharge / 100) + totalIPPrice * timeMultiplier;
  }, [customCPU, customRAM, customDISK, customDATAT, surcharge, totalIPPrice, smuser]);

  const calculateOriginalPrice = useCallback((config, priceKey) => {
    if (!config) return 0;
    return customCPU * config.cpu_rate + customRAM * config.ram_rate + customDISK * (config[priceKey] ?? 0) + customDATAT * 1;
  }, [customCPU, customRAM, customDISK, customDATAT]);

  const getCustomFinalPrice = useCallback((config, priceKey) =>
    calculateCustomPrice(config, priceKey, discountRate || 0, newMachineTime),
    [calculateCustomPrice, discountRate, newMachineTime]);

  const getDiscountKey  = useCallback(() => `discount_price_${newMachineTime}`, [newMachineTime]);
  const getActiveDiscount = useCallback(() => {
    const key = getDiscountKey();
    return activeCusConfig?.[key] ?? 0;
  }, [activeCusConfig, getDiscountKey]);

  const determineDiscountRate = useCallback((ct, dt, time) => {
    const cfg = getCusConfig(ct, dt, cusConfigs);
    const key = `discount_price_${time}`;
    const val = ct === 2 && dt === "hdd" ? cfg?.[key]?.custom_discount : cfg?.[key];
    setDiscountRate(val);
  }, [cusConfigs]);

  const onChangeLocation = useCallback((id) => {
    if (!custConfigData || !custConfigData.filter) return;
    const filter = (cid) => custConfigData.filter((i) => i.cid === cid && id == i.server_location);
    const stHdd = filter(1); const stSsd = filter(5); const stNvm = filter(6);
    const cpHdd = filter(3); const cpSsd = filter(7); const cpNvm = filter(8);
    const rmHdd = filter(4); const rmSsd = filter(9); const rmNvm = filter(10);
    setCustConfigStdHDD(stHdd); setCustConfigStdSSD(stSsd); setCustConfigStdNVME(stNvm);
    setCustConfigCpuHDD(cpHdd); setCustConfigCpuSSD(cpSsd); setCustConfigCpuNVME(cpNvm);
    setCustConfigRamHDD(rmHdd); setCustConfigRamSSD(rmSsd); setCustConfigRamNVME(rmNvm);
    if (activePage !== "Custom Configure") return;
    const configForCheck =
      activeButton === "Standard"      ? (diskType === "ssd" ? stSsd : diskType === "nvme" ? stNvm : stHdd)
      : activeButton === "CPU Proactive" ? (diskType === "ssd" ? cpSsd : diskType === "nvme" ? cpNvm : cpHdd)
      :                                    (diskType === "ssd" ? rmSsd : diskType === "nvme" ? rmNvm : rmHdd);
    if (!configForCheck || configForCheck.length === 0) {
      setStockAvailableStatus(false);
      showToast("Stock is currently unavailable for this location.", isMobile);
    } else { setStockAvailableStatus(true); }
  }, [custConfigData, activePage, activeButton, diskType, isMobile]);

  useEffect(() => {
    if (custConfigData && selectedLocation) onChangeLocation(selectedLocation);
  }, [custConfigData, selectedLocation]);

  const apiPost = useCallback(async (url, payload) => {
    const enc = await apiEncryptRequest(payload);
    const res = await instance.post(url, enc);
    return decryptData(res.data);
  }, []);

  const transformData = (data) =>
    Object.entries(data).reduce((acc, [key, value]) => {
      acc[key] = value.map((item) => { const [name, id] = Object.entries(item)[0]; return { name, id }; });
      return acc;
    }, {});

  const categoriseVMs = (vmArray) => {
    const acc = { s: [], c: [], r: [], s5: [], s6: [], c7: [], c8: [], r9: [], r10: [] };
    vmArray.forEach((item) => {
      const { cid, ...rest } = item;
      if      (cid === 1)  acc.s.push({ cid, ...rest });
      else if (cid === 3)  acc.c.push({ cid, ...rest });
      else if (cid === 4)  acc.r.push({ cid, ...rest });
      else if (cid === 5)  acc.s5.push({ cid, ...rest });
      else if (cid === 6)  acc.s6.push({ cid, ...rest });
      else if (cid === 7)  acc.c7.push({ cid, ...rest });
      else if (cid === 8)  acc.c8.push({ cid, ...rest });
      else if (cid === 9)  acc.r9.push({ cid, ...rest });
      else if (cid === 10) acc.r10.push({ cid, ...rest });
    });
    return acc;
  };

  const categoriseStocks = (stocks) => {
    const acc = { s: [], s5: [], s6: [], c: [], c7: [], c8: [], r: [], r9: [], r10: [] };
    stocks.forEach((item) => {
      const { cid, ...rest } = item;
      if      (cid === 1)  acc.s.push({ cid, ...rest });
      else if (cid === 5)  acc.s5.push({ cid, ...rest });
      else if (cid === 6)  acc.s6.push({ cid, ...rest });
      else if (cid === 3)  acc.c.push({ cid, ...rest });
      else if (cid === 7)  acc.c7.push({ cid, ...rest });
      else if (cid === 8)  acc.c8.push({ cid, ...rest });
      else if (cid === 4)  acc.r.push({ cid, ...rest });
      else if (cid === 9)  acc.r9.push({ cid, ...rest });
      else if (cid === 10) acc.r10.push({ cid, ...rest });
    });
    return acc;
  };

  const CreateLi = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiPost("/create_li", { user_id: smuser.id });
      setStandardRates(data.ratedata1); setCpuIntensiveRates(data.ratedata3); setRamIntensiveRates(data.ratedata4);
      const { s, c, r } = categoriseVMs(Object.values(data.mergedData));
      setSSsd(s); setCHdd(c); setRHdd(r);
      const osTemp = data.osTemp;
      if (osTemp["Ubuntu_fa-ubuntu"]) { setUbantuOS(osTemp["Ubuntu_fa-ubuntu"]); setFedoraOS(osTemp["Fedora_fa-windows"]); }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [smuser?.id, apiPost]);

  const CreateVm = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiPost("/create-vm", { user_id: smuser.id });
      setSurcharge(data.surcharge.surcharge); localStorage.setItem("surcharge", data.surcharge.surcharge);
      setIPPrice(data.ip_price); setAllOsTemplete(transformData(data.osTemp));
      setStandardRates(data.ratedata1); setCpuIntensiveRates(data.ratedata3); setRamIntensiveRates(data.ratedata4);
      setStdCusHDD(data.ratedata1); setStdCusSSD(data.ratedata5); setStdCusNVM(data.ratedata6);
      setCpuCusHDD(data.ratedata3); setCpuCusSSD(data.ratedata7); setCpuCusNVM(data.ratedata8);
      setRamCusHDD(data.ratedata4); setRamCusSSD(data.ratedata9); setRamCusNVM(data.ratedata10);
      const { s, s5, s6, c, c7, c8, r, r9, r10 } = categoriseVMs(Object.values(data.mergedData));
      setSHdd(s); setSSsd(s5); setSNvm(s6); setCHdd(c); setCSsd(c7); setCNvm(c8); setRHdd(r); setRSsd(r9); setRNvm(r10);
      const osTemp = data.osTemp;
      if (osTemp["Ubuntu_fa-ubuntu"]) {
        setUbuntu_fa(osTemp["Ubuntu_fa-ubuntu"]); setWindows_fa(osTemp["Windows_fa-windows"]);
        setRedhat_fa(osTemp["Redhat_fa-redhat"]); setCentOS_fa(osTemp["CentOS_fa-centos"]);
        setLinux_fa(osTemp["linux_fa-linux"]);    setRocky_fa(osTemp["Rocky_fa-rokcy"]);
        setFedora_fa(osTemp["Fedora_fa-fedora"]); setDebian_fa(osTemp["Debian_fa-debian"]);
      }
      const { s: sH, s5: sSS, s6: sNS, c: cH, c7: cSS, c8: cNS, r: rH, r9: rSS, r10: rNS } = categoriseStocks(data.stocks);
      setSHddStocks(sH); setSSsdStocks(sSS); setSNvmStocks(sNS);
      setCHddStocks(cH); setCSsdStocks(cSS); setCNvmStocks(cNS);
      setRHddStocks(rH); setRSsdStocks(rSS); setRNvmStocks(rNS);
      setCustConfigData(data.custom_configure); setServerLocaiton(data.server_locaiton);
      setSelectedLocation(data.server_locaiton[0].id); setUpnetPercentage(data.rewards.reward);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [smuser?.id, apiPost]);

  useEffect(() => {
    window.scrollTo(0, 0); setUserSystem(window.navigator.userAgent); localStorage.removeItem("machineTime");
    if (smuser) { setPlatformSelected(smuser.platform_status); smuser.platform_status === "1" ? CreateLi() : CreateVm(); }
  }, [smuser?.id]);

  const syncDiscountOnTimeChange = useCallback((time) => {
    const list = getVmList(activePage, diskType, vmLists);
    if (!list || list.length === 0) return;
    setDiscountRate(list[0]?.[`discount_price_${time}`] ?? "0");
  }, [activePage, diskType, vmLists]);

  const resetSelection = useCallback(() => {
    setSelectedCPU(0); setSelectedRAM(0); setSelectedSSD(0); setSelectedDT(null);
    setFinalAmount("0"); setDiscountRate("0"); setMachineAmt("0");
    setSelectedIdx(null); setNewMachineConfigId(null);
  }, []);

  const handleCardSelect = useCallback((item, idx, price) => {
    setSelectedCPU(item.cpu); setSelectedRAM(item.ram); setSelectedSSD(item.hdd); setSelectedDT(item.data_transfer);
    setFinalAmount(price * newMachineTime); setDiscountRate(item[`discount_price_${newMachineTime}`]);
    setMachineAmt(price); setSelectedIdx(idx); setNewMachineConfigId(item.config_id);
    setCouponCode("");
    setCouponDiscount(0);
  }, [newMachineTime]);

  const handleRedirect = () => { localStorage.setItem("machineTime", new Date().toISOString()); window.location.href = "/vm-machine"; };

  const CreateMachine_Li = useCallback(async (numOfIp, jumpServerValue) => {
    if (smuser.total_credit < finalAmount) { setShowIPOptionPopup(false); showToast("Oops! Your wallet balance is low, Please Add Money to create machine", isMobile); return; }
    const valid = newMahineConfigId && newMachineName && newMahineOs && newMahineOs !== "Select" && finalAmount !== "0" && newMachineTime && selectedLocation;
    if (!valid) {
      if (!newMahineConfigId) showToast("Machine Configuration Required", isMobile);
      else if (!newMachineName) showToast("Machine Name Required", isMobile);
      else if (!newMahineOs || newMahineOs === "Select") showToast("Machine OS is Required", isMobile);
      else if (!selectedLocation) showToast("Server Location is Required", isMobile);
      else showToast("All fields are Required", isMobile);
      setShowIPOptionPopup(false); return;
    }
    setLoading(true);
    // const finAmt = finalAmount - (finalAmount * discountRate) / 100 + totalIPPrice * newMachineTime;
    const extraIPCost = Math.max(0, selectedNoIP - 1) * iPPrice * newMachineTime; // 1st IP free
    const finAmt = finalAmount - (finalAmount * discountRate) / 100 + extraIPCost;

    const payload = { uuid: newMahineOs, jump_server: jumpServerValue || "0", config: newMahineConfigId, name: `${newMachineName}${Math.floor(100 + Math.random() * 900)}`, machine_val: newMahineOs, machine_price: finAmt, plan_time: newMachineTime, user_id: smuser.id, disk_type: diskType, server_location: selectedLocation, num_ip: numOfIp,coupon_code: couponCode,coupon_discount: couponDiscount,  };
    try {
      const url = smuser.platform_status === "1" ? "/vm/store-li" : "/vm/store-vm";
      const data = await apiPost(url, payload);
      if (smuser.platform_status === "1") { if (data.status) window.location.href = "/vm-machine"; } else { setTimeout(handleRedirect, 5000); }
    } catch { showToast("Oops! Something went wrong. Please try again later or contact support.", isMobile); }
    setLoading(false);
  }, [smuser, finalAmount, newMahineConfigId, newMachineName, newMahineOs, newMachineTime, selectedLocation, diskType, discountRate, totalIPPrice, apiPost, isMobile]);

  const CreateCustomMachine_Li = useCallback(async (numOfIp, jumpServerValue) => {
    setLoading(true);
    if (smuser.phoneverify !== 1) { setShowPhoneVerify(true); setShowIPOptionPopup(false); setLoading(false); return; }
    const priceKey = getDiskPriceKey(diskType);
    const cfg = activeCusConfig;
    if (!cfg?.cpu_rate) { setLoading(false); return; }
    const base = customCPU * cfg.cpu_rate + customRAM * cfg.ram_rate + customDISK * cfg[priceKey] + customDATAT;
    const disc = (base * (discountRate || 0)) / 100;
    const amount = smuser.country === "India" ? base - disc + totalIPPrice * newMachineTime : (base - disc) * (1 + surcharge / 100) + totalIPPrice * newMachineTime;
    if (smuser.total_credit < amount) { showToast("Oops! Your wallet balance is low, Please Add Money to create machine", isMobile); setShowIPOptionPopup(false); setLoading(false); return; }
    const valid = newMachineName && newMahineOs && newMahineOs !== "Select" && amount !== 0 && customDISK && customCPU && customRAM && customDATAT && selectedLocation;
    if (!valid) {
      if (!newMachineName) showToast("Machine Name Required", isMobile);
      else if (!newMahineOs) showToast("Machine OS is Required", isMobile);
      else if (!selectedLocation) showToast("Server Location is Required", isMobile);
      else showToast("Must select configuration", isMobile);
      setShowIPOptionPopup(false); setLoading(false); return;
    }
    const subConfig = `${configType === 1 ? "standard" : configType === 2 ? "cpu" : "ram"}${diskType === "ssd" ? "2" : diskType === "nvme" ? "3" : "1"}`;
    const payload = { flag: 0, uuid: newMahineOs, jump_server: jumpServerValue ? 1 : 0, name: `${newMachineName}${Math.floor(100 + Math.random() * 900)}`, machine_val: newMahineOs, machine_price: amount, plan_time: newMachineTime, user_id: smuser.id, hdde: customDISK, cpue: customCPU, rame: customRAM, data_transfer: customDATAT, disk_type: diskType, sub_config_type: subConfig, server_location: selectedLocation, num_ip: numOfIp };
    try {
      const url = smuser.platform_status === "1" ? "/vm/store-li" : "/vm/store-vm";
      const data = await apiPost(url, payload);
      if (smuser.platform_status === "1") { if (data.status) window.location.href = "/vm-machine"; } else { setTimeout(() => { window.location.href = "/vm-machine"; }, 3000); }
    } catch { }
    setShowIPOptionPopup(false); setLoading(false);
  }, [smuser, activeCusConfig, diskType, customCPU, customRAM, customDISK, customDATAT, discountRate, totalIPPrice, newMachineTime, surcharge, newMachineName, newMahineOs, selectedLocation, configType, apiPost, isMobile]);

  const PhoneVerifyCall = useCallback(async () => {
    setLoading(true);
    if (!phone) { toast.error("All fields are required!"); setLoading(false); return; }
    try {
      const data = await apiPost("/getmobileotp", { user_id: smuser.id, phone, otp: phoneOTP, name: smuser.name });
      if (phoneOtpSent) { if (data.status) { showToast(data.message, isMobile); setShowPhoneVerify(false); const res = await instance.post("/changescurrency", { country: smuser.prefer_currency, user_id: smuser.id }); updateCurrencyRate(res.data.native_credit); updateUserDetails(res.data.response); } }
      else { if (data.status) { setPhoneOtpSent(true); showToast(data.message, isMobile); } }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [phone, phoneOTP, phoneOtpSent, smuser, apiPost, isMobile, updateCurrencyRate, updateUserDetails]);

  const handleLaunchClick = useCallback(() => {
    if (smuser.phoneverify !== 1) { setShowPhoneVerify(true); return; }
    const ready = newMachineName && newMahineOs && newMahineOs !== "Select";
    if (!ready) { if (!newMachineName) showToast("Machine Name Required", isMobile); else showToast("Machine OS is Required", isMobile); return; }
    if (!stockAvailableStatus) { showToast("Stock is currently unavailable for this location.", isMobile); return; }
    if (activePage !== "Custom Configure" && selectedSSD < 20) { showToast("Please select minimum 20 GB Disk Storage.", isMobile); return; }
    const serverCost = finalAmount - (finalAmount * discountRate) / 100;
    const fAmount    = serverCost - (serverCost * upnetPercentage) / 100 - smuser.total_credit;
    setAddToWalletAmt(fAmount);
    if (fAmount > smuser.total_credit && smuser.reward_points != null && smuser.reward_points > 0) { ShowUpnetCreditContentPopup(true); }
    else { setCounter(1); setShowIPOptionPopup(true); }
  }, [smuser, newMachineName, newMahineOs, stockAvailableStatus, activePage, selectedSSD, finalAmount, discountRate, upnetPercentage, isMobile]);

  const launchPortgate = useCallback(() => { activePage === "Custom Configure" ? CreateCustomMachine_Li(0, 0) : CreateMachine_Li(0, 0); }, [activePage, CreateCustomMachine_Li, CreateMachine_Li]);
  const launchCloud    = useCallback((jumpServer = 0) => { activePage === "Custom Configure" ? CreateCustomMachine_Li(selectedNoIP, jumpServer) : CreateMachine_Li(selectedNoIP, jumpServer); }, [activePage, selectedNoIP, CreateCustomMachine_Li, CreateMachine_Li]);

  const handleDiskChange = useCallback((newDisk) => { setDisktype(newDisk); resetSelection(); setCustomDISK(0); }, [resetSelection]);

  const customPrice = useMemo(() => {
    const priceKey = getDiskPriceKey(diskType);
    return activeCusConfig ? getCustomFinalPrice(activeCusConfig, priceKey) : 0;
  }, [activeCusConfig, diskType, getCustomFinalPrice]);

  const customPriceFormatted = useMemo(() => {
    if (!smuser || !appCurrency || !activeCusConfig) return null;
    return currencyReturn({ price: customPrice, symbol: smuser.prefer_currency, rates: appCurrency });
  }, [smuser, appCurrency, customPrice]);

  const ipCardPrice = useMemo(() => {
    if (activePage === "Custom Configure") return customPrice;
    const india = finalAmount - (finalAmount * discountRate) / 100;
    return smuser?.country === "India" ? india : india * (1 + surcharge / 100);
  }, [activePage, customPrice, finalAmount, discountRate, surcharge, smuser]);

  // const ipCardPriceWithIP = useMemo(() => {
  //   if (activePage === "Custom Configure") return customPrice;
  //   const india = finalAmount - (finalAmount * discountRate) / 100 + totalIPPrice * newMachineTime;
  //   return smuser?.country === "India" ? india : (finalAmount - (finalAmount * discountRate) / 100) * (1 + surcharge / 100) + totalIPPrice * newMachineTime;
  // }, [activePage, customPrice, finalAmount, discountRate, totalIPPrice, newMachineTime, surcharge, smuser]);

 // REPLACE the existing ipCardPriceWithIP useMemo:
  const ipCardPriceWithIP = useMemo(() => {
    if (activePage === "Custom Configure") return customPrice;
    const extraIPCost = Math.max(0, counter - 1) * iPPrice * newMachineTime; // 1st IP free
    const base = finalAmount - (finalAmount * discountRate) / 100;
    const withSurcharge = smuser?.country === "India" ? base : base * (1 + surcharge / 100);
    const withIP = withSurcharge + extraIPCost;
    return Math.max(0, withIP - couponDiscount); // ← subtract coupon here
  }, [activePage, customPrice, finalAmount, discountRate, counter, iPPrice, newMachineTime, surcharge, smuser, couponDiscount]);

  const isLiPlatform = smuser?.platform_status === "1";

  const cloudOsList = useMemo(() => [
    { label: "Ubuntu",    icon: "/images/admin/02-VM/ubanto-logo.svg",  options: ubuntu_fa, value: newMahineOs, onChange: setNewMachineOs },
    { label: "Fedora",    icon: "/fedora-icon.svg",                     options: fedora_fa, value: newMahineOs, onChange: setNewMachineOs, iconStyle: { marginLeft: 5, width: isMobile ? 40 : 100, height: isMobile ? 40 : 60 } },
    { label: "CentOS",    icon: "/images/admin/02-VM/centos-logo.svg",  options: centOS_fa, value: newMahineOs, onChange: setNewMachineOs },
    { label: "Redhat",    icon: "/images/admin/02-VM/centos-logo.svg",  options: redhat_fa, value: newMahineOs, onChange: setNewMachineOs },
    { label: "Rocky",     icon: "/rocky-icon.svg",                      options: rocky_fa,  value: newMahineOs, onChange: setNewMachineOs },
    { label: "Almalinux", icon: "/alma-icon.svg",                       options: linux_fa,  value: newMahineOs, onChange: setNewMachineOs },
    { label: "Debian",    icon: "/debian-icon.svg",                     options: debian_fa, value: newMahineOs, onChange: setNewMachineOs },
  ], [ubuntu_fa, fedora_fa, centOS_fa, redhat_fa, rocky_fa, linux_fa, debian_fa, newMahineOs, isMobile]);

  const liOsList = useMemo(() => [
    { label: "Ubuntu", icon: "/images/admin/02-VM/ubanto-logo.svg", options: ubantuOS, value: ubantu_machine, onChange: (v) => { setNewMachineOs(v); setUbantu_machine(v); setFedora_machine("select"); } },
    { label: "Fedora", icon: "/images/admin/02-VM/window-logo.svg", options: fedoraOS, value: fedora_machine, onChange: (v) => { setNewMachineOs(v); setUbantu_machine("Select"); setFedora_machine(v); } },
  ], [ubantuOS, fedoraOS, ubantu_machine, fedora_machine]);

  const osList = isLiPlatform ? liOsList : cloudOsList;

  // const IpOptionCard = memo(({ title, showCounter, onLaunch }) => (
  //   <div className="box" style={{ marginRight: 15, marginTop: -20, width: "15rem" }}>
  //     <div style={{ position: "relative", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", padding: "20px 0px", zIndex: 1 }}>
  //       <img src="/images/admin/01-home/server.svg" className="bg-image" style={{ position: "absolute", width: "100%", height: "100%", left: 0, right: 0, top: 0, zIndex: -1 }} />
  //       <div className="machine-icon-edit-profile" style={{ width: 94, height: 94 }}>
  //         <img src="/images/admin/01-home/cpu.svg" alt="" style={{ width: 40, height: 40 }} />
  //       </div>
  //       <div className="machine-titles theme-bg-orange" style={{ width: title.length > 8 ? 116 : 100, fontWeight: 600 }}>{title}</div>
  //       <div className="machine-name theme-color-blue">{""}</div>
  //       <div className="machine-config-badge" style={{ color: "white", backgroundColor: "#154e7a", fontWeight: 600, marginTop: 10 }}>
  //         {smuser && appCurrency && currencyReturn({ price: showCounter ? ipCardPriceWithIP : ipCardPrice, symbol: smuser.prefer_currency, rates: appCurrency })} / Month
  //       </div>
  //       <div className="line-shape" style={{ width: "95%", height: 2, borderRadius: "50%", marginTop: 10, background: "linear-gradient(90deg, rgba(190,190,190,0), rgba(190,190,190,1), rgba(190,190,190,0))" }} />
  //       <div className="machine-ip" style={{ marginTop: 8, textAlign: "center", color: "#154e7a", fontWeight: 600 }}>
  //         Public IP
  //         {showCounter && (
  //           <div className="counter-section" style={{ marginTop: 10 }}>
  //             <button onClick={() => setCounter((c) => Math.max(1, c - 1))} className="counter-btn">−</button>
  //             <span style={{ margin: "0 15px" }}>{counter}</span>
  //               <button onClick={() => setCounter((c) => Math.min(3, c + 1))} className="counter-btn">+</button>
  //           </div>
  //         )}
  //       </div>
  //       {title === "Portgate VM" && <div className="machine-ip" style={{ marginTop: 8 }}>Required Domain</div>}
  //       {title !== "Portgate VM" && <div className="machine-ip" style={{ marginTop: 8 }}>Domain not Required</div>}
  //       <div className="machine-date" style={{ marginTop: 8 }}>
  //         {title === "Portgate VM" ? <><div>HTTP to HTTPS Redirect : Enabled</div><div>SSL : Enabled</div></> : <><div>Unmanaged Cloud VM</div><div>SSL Managed by VM Admin</div></>}
  //       </div>
  //       <div className="log-in" style={{ marginTop: 10, marginLeft: "-8rem", justifyContent: "center" }} onClick={onLaunch}>
  //         <a href="#" className="media-link">
  //           <div className="media-banner" style={{ width: "auto", height: 50, marginTop: 5, marginLeft: "10rem" }}>
  //             <img className="normal-banner"    src="/admin/images/admin/wallet/add-money-btn.png" alt="" style={{ marginTop: 5, width: "9rem", height: "3rem" }} />
  //             <img className="hover-img-banner" src="/admin/images/admin/wallet/recharge-btn.png" alt="" style={{ marginTop: 5, width: "9rem", height: "3rem" }} />
  //             <span className="login-text" style={{ color: "white", fontSize: 15, marginTop: 5, fontWeight: 600 }}>Launch Machine</span>
  //           </div>
  //         </a>
  //       </div>
  //       <br /><br />
  //     </div>
  //   </div>
  // ));
  // ─── IpOptionCard — defined OUTSIDE CreateMachine ──────────────────────────
  const IpOptionCard = memo(({ title, showCounter, onLaunch, counter, setCounter, ipCardPriceWithIP, ipCardPrice, smuser, appCurrency }) => (
    <div className="box" style={{ marginRight: 15, marginTop: -20, width: "15rem" }}>
      <div style={{ position: "relative", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", padding: "20px 0px", zIndex: 1 }}>
        <img src="/images/admin/01-home/server.svg" className="bg-image" style={{ position: "absolute", width: "100%", height: "100%", left: 0, right: 0, top: 0, zIndex: -1 }} />
        <div className="machine-icon-edit-profile" style={{ width: 94, height: 94 }}>
          <img src="/images/admin/01-home/cpu.svg" alt="" style={{ width: 40, height: 40 }} />
        </div>
        <div className="machine-titles theme-bg-orange" style={{ width: title.length > 8 ? 116 : 100, fontWeight: 600 }}>{title}</div>
        <div className="machine-name theme-color-blue">{""}</div>
        <div className="machine-config-badge" style={{ color: "white", backgroundColor: "#154e7a", fontWeight: 600, marginTop: 10 }}>
          {smuser && appCurrency && currencyReturn({ price: showCounter ? ipCardPriceWithIP : ipCardPrice, symbol: smuser.prefer_currency, rates: appCurrency })} / Month
        </div>
        <div className="line-shape" style={{ width: "95%", height: 2, borderRadius: "50%", marginTop: 10, background: "linear-gradient(90deg, rgba(190,190,190,0), rgba(190,190,190,1), rgba(190,190,190,0))" }} />
        <div className="machine-ip" style={{ marginTop: 8, textAlign: "center", color: "#154e7a", fontWeight: 600 }}>
          Public IP
          {showCounter && (
            <div className="counter-section" style={{ marginTop: 10 }}>
              <button onClick={() => setCounter((c) => Math.max(1, c - 1))} className="counter-btn">−</button>
              <span style={{ margin: "0 15px" }}>{counter}</span>
              <button onClick={() => setCounter((c) => Math.min(3, c + 1))} className="counter-btn">+</button>
            </div>
          )}
          {showCounter && (
            <div style={{ fontSize: 11, color: "#e97730", fontWeight: 700, marginTop: 4 }}>
              1 IP Free · Extra IPs charged
            </div>
          )}
        </div>
        {/* {title === "Portgate VM" && <div className="machine-ip" style={{ marginTop: 8 }}>Required Domain</div>}
        {title !== "Portgate VM" && <div className="machine-ip" style={{ marginTop: 8 }}>Domain not Required</div>}
        <div className="machine-date" style={{ marginTop: 8 }}>
          {title === "Portgate VM"
            ? <><div>HTTP to HTTPS Redirect : Enabled</div><div>SSL : Enabled</div></>
            : <><div>Unmanaged Cloud VM</div><div>SSL Managed by VM Admin</div></>}
        </div> */}
        <div className="log-in" style={{ marginTop: 10, marginLeft: "-8rem", justifyContent: "center" }} onClick={onLaunch}>
          <a href="#" className="media-link">
            <div className="media-banner" style={{ width: "auto", height: 50, marginTop: 5, marginLeft: "10rem" }}>
              <img className="normal-banner"    src="/admin/images/admin/wallet/add-money-btn.png" alt="" style={{ marginTop: 5, width: "9rem", height: "3rem" }} />
              <img className="hover-img-banner" src="/admin/images/admin/wallet/recharge-btn.png" alt="" style={{ marginTop: 5, width: "9rem", height: "3rem" }} />
              <span className="login-text" style={{ color: "white", fontSize: 15, marginTop: 5, fontWeight: 600 }}>Launch Machine</span>
            </div>
          </a>
        </div>
        <br /><br />
      </div>
    </div>
  ));

  const renderPriceDisplay = () => {
    if (activePage === "Custom Configure") {
      const discount = getActiveDiscount();
      if (!smuser || !appCurrency || !activeCusConfig) return null;
      const priceKey  = getDiskPriceKey(diskType);
      const origPrice = calculateOriginalPrice(activeCusConfig, priceKey);
      return (
        <div className="price-container" style={{ marginBottom: 10, marginLeft: 7 }}>
          {discount > 0 && <div className="price-old"><span style={{ textDecoration: "line-through", fontSize: 15, marginRight: 10 }}>{currencyReturn({ price: origPrice, symbol: smuser.prefer_currency, rates: appCurrency })}</span>{customPriceFormatted}</div>}
          {!discount && <div className="price-old">{customPriceFormatted}</div>}
          {discount > 0 && <div className="price-new"><span style={{ zIndex: 9 }}>{discount} % OFF</span></div>}
        </div>
      );
    }
    if (!smuser || !appCurrency || finalAmount <= 0) return null;
    // const finalDisplay = smuser.country === "India"
    //   ? finalAmount - (finalAmount * discountRate) / 100
    //   : (finalAmount - (finalAmount * discountRate) / 100) * (1 + surcharge / 100);

    const baseAfterTimeDiscount = smuser.country === "India"
      ? finalAmount - (finalAmount * discountRate) / 100
      : (finalAmount - (finalAmount * discountRate) / 100) * (1 + surcharge / 100);
  
    const finalDisplay = Math.max(0, baseAfterTimeDiscount - couponDiscount);
    return (
      <div className="price-container" style={{ marginBottom: 10, marginLeft: 7 }}>
        <div className="price-old">
          {discountRate > 0 && <span style={{ textDecoration: "line-through", fontSize: 16, marginRight: 9 }}>{currencyReturn({ price: finalAmount, symbol: smuser.prefer_currency, rates: appCurrency })}</span>}
          <span style={{ zIndex: 9, fontSize: 20 }}>{currencyReturn({ price: finalDisplay, symbol: smuser.prefer_currency, rates: appCurrency })}</span>
        </div>
        {discountRate > 0 && <div className="price-new"><span style={{ zIndex: 9, fontSize: 20 }}>{discountRate}% OFF</span></div>}
      </div>
    );
  };
  

  const renderCustomDiskSelect = () => {
    const prefix = activeButton === "Standard" ? "s" : activeButton === "CPU Proactive" ? "c" : "r";
    const hddSt = allStocks[`${prefix}HddStocks`]?.[0];
    const ssdSt = allStocks[`${prefix}SsdStocks`]?.[0];
    const nvmSt = allStocks[`${prefix}NvmStocks`]?.[0];
    return (
      <select name="plan_time" value={diskType}
        style={{ borderRadius: 30, padding: "5px", fontSize: isMobile ? 16 : 30, border: "2px solid #e97730", color: "#144e7b", fontWeight: 700, backgroundColor: "transparent" }}
        onChange={(e) => { setDisktype(e.target.value); setCustomDISK(0); }}>
        {hddSt?.stocks === 1 && <option value="hdd">HDD Disk</option>}
        {ssdSt?.stocks === 1 && <option value="ssd">SSD Disk</option>}
        {nvmSt?.stocks === 1 && <option value="nvme">NVMe Disk</option>}
      </select>
    );
  };

  const handleTimeChange = useCallback((value) => {
    setFinalAmount(machineAmt * value); setNewMachineTime(value);
    if (activePage === "Custom Configure") { determineDiscountRate(configType, diskType, value); }
    else { syncDiscountOnTimeChange(value); }
  }, [machineAmt, activePage, configType, diskType, determineDiscountRate, syncDiscountOnTimeChange]);

  // ─── Render ───────────────────────────────────────────────────────────────────
  return (
    <div style={{ width: "100%", minHeight: "75rem", position: "relative", backgroundImage: "url(/main-bg.jpg)", backgroundSize: "cover", backgroundRepeat: "round", backgroundBlendMode: "overlay" }}>
      <style>{RESPONSIVE_CSS}</style>
      <div style={{ position: "fixed", bottom: "2rem", zIndex: 9999999999 }}>
        <Toaster position={isMobile ? "top-center" : "bottom-right"} reverseOrder={false} />
      </div>

      {showPhoneVerify && (
        <div style={{ display: "grid", justifyItems: "center" }}>
          <div style={{ backgroundImage: "url(/main-bg.jpg)", backgroundSize: "cover", top: "20%", position: "absolute", zIndex: 999999999, width: isMobile ? "90%" : "35%", backdropFilter: "blur(5px)", height: "20rem", borderRadius: 12, border: "2px solid #e97730" }}>
            <div style={{ display: "grid", justifyItems: "center" }}>
              <button style={{ color: "#035189", position: "absolute", right: 0, border: "none", backgroundColor: "transparent" }} onClick={() => setShowPhoneVerify(false)}>X</button>
              <h4 style={{ marginTop: 10 }}>Verify your Phone</h4>
              <div className="input-container" style={{ marginTop: 15, border: "2px solid #fff", backgroundColor: "#e97730", outline: "2px solid #e97730" }}>
                <img src="/images/phone.svg" alt="" className="imgIcon-signup" />
                <select name="plan_time" style={{ borderRadius: 30, marginRight: 10, padding: "10px 15px", border: "2px solid white" }}><option value="+91">IND</option></select>
                <input type="number" className="input-signup" placeholder="Enter phone" disabled={phoneOtpSent} value={phone} onChange={(e) => setPhone(e.target.value)} />
              </div>
              {phoneOtpSent && (
                <div className="input-container" style={{ marginTop: 25, border: "2px solid #fff", backgroundColor: "#e97730", outline: "2px solid #e97730" }}>
                  <input type="number" className="input-signup" placeholder="Enter OTP" value={phoneOTP} onChange={(e) => setPhoneOTP(e.target.value)} style={{ textAlign: "center" }} />
                </div>
              )}
              <button style={{ color: "white", width: "8rem", height: 45, backgroundColor: "#035189", borderRadius: 25, border: "2px solid #ffff", outline: "2px solid #035189", marginTop: 25 }} onClick={PhoneVerifyCall}>
                {phoneOtpSent ? "Verify OTP" : "Send OTP"}
              </button>
            </div>
          </div>
        </div>
      )}

     {showIPOptionPopup && (
        <div style={{ display: "grid", justifyItems: "center" }}>
          <div style={{ backdropFilter: "blur(30px)", borderRadius: 12, border: "2px solid #e97730", top: "12%", position: "absolute", zIndex: 999999999, width: isMobile ? "85%" : "60%", height: isMobile ? "60rem" : "40rem" }}>
            <button style={{ zIndex: 9, position: "absolute", backgroundColor: "transparent", border: "none", right: 0 }} onClick={() => setShowIPOptionPopup(false)}>
              <FaX style={{ marginTop: 5, color: "#e97730", fontSize: 19 }} />
            </button>
            <div style={{ display: isMobile ? undefined : "flex", justifyContent: isMobile ? undefined : "center", gap: isMobile ? undefined : 100, padding: isMobile ? undefined : "3rem 3rem 1rem 3rem" }}>
              <IpOptionCard
                title="Cloud VM"
                showCounter={true}
                onLaunch={() => launchCloud(0)}
                counter={counter}
                setCounter={setCounter}
                ipCardPriceWithIP={ipCardPriceWithIP}
                ipCardPrice={ipCardPrice}
                smuser={smuser}
                appCurrency={appCurrency}
              />
              <IpOptionCard
                title="Jump Server"
                showCounter={true}
                onLaunch={() => launchCloud(1)}
                counter={counter}
                setCounter={setCounter}
                ipCardPriceWithIP={ipCardPriceWithIP}
                ipCardPrice={ipCardPrice}
                smuser={smuser}
                appCurrency={appCurrency}
              />
            </div>
            {/* Single shared coupon box */}
            <div style={{ display: "flex", justifyContent: "center", paddingBottom: "1rem" }}>
              <div style={{ width: isMobile ? "90%" : "34rem" }}>
                <CouponBox
                  orderAmount={finalAmount}
                  isMobile={false}
                  onApply={({ couponCode: cc, discount }) => {
                    setCouponCode(cc);
                    setCouponDiscount(discount);
                  }}
                  onRemove={() => {
                    setCouponCode("");
                    setCouponDiscount(0);
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {isShowUpnetCreditContentPopup && smuser && appCurrency && (
        <div style={{ top: isMobile ? "95%" : "10%", left: isMobile ? "5%" : "28%", position: "absolute", zIndex: 99999, width: isMobile ? "23rem" : "60rem", backdropFilter: "blur(20px)", height: "30rem", borderRadius: 12, border: "2px solid #e97730" }}>
          <button style={{ zIndex: 9, position: "absolute", backgroundColor: "transparent", border: "none", right: 0 }} onClick={() => ShowUpnetCreditContentPopup(false)}>
            <FaX style={{ color: "#e97730", fontSize: 19 }} />
          </button>
          <div style={{ position: "relative", textAlign: "center" }}>
            <h4 style={{ color: "white", position: "absolute", top: "13rem", left: "50%", width: "85%", transform: "translate(-50%,-50%)", padding: 20, outline: "4px solid #035189", border: "4px solid #fff", borderRadius: 30, backgroundColor: "#035189", fontSize: 18, fontWeight: "normal", lineHeight: "2em", textAlign: "left" }}>
              <strong>UPNET Credits Applied:</strong> Credits available.<br />
              <strong>Usage:</strong> {upnetPercentage}%<br />
              <strong>Server Cost:</strong> {currencyReturn({ price: finalAmount - (finalAmount * discountRate) / 100, symbol: smuser.prefer_currency, rates: appCurrency })}<br />
              <strong>Available Credits:</strong> {currencyReturn({ price: smuser.reward_points, symbol: smuser.prefer_currency, rates: appCurrency })}<br />
              <strong>Amount to Add:</strong> {currencyReturn({ price: addToWalletAmt, symbol: smuser.prefer_currency, rates: appCurrency })}
            </h4>
          </div>
          <div style={{ marginTop: "24rem", display: "flex", justifyContent: "center" }}>
            <button style={{ zIndex: 9, fontSize: 20, color: "white", padding: 10, height: 55, backgroundColor: "#e97730", outline: "4px solid #e97730", border: "4px solid #fff", borderRadius: 30 }}
              onClick={() => { ShowUpnetCreditContentPopup(false); navigate("/wallet", { state: { isFromCredits: true, upnetCreditAmount: addToWalletAmt } }); }}>
              Add {currencyReturn({ price: addToWalletAmt, symbol: smuser.prefer_currency, rates: appCurrency })} to your UPNET Wallet
            </button>
          </div>
        </div>
      )}

      {showJumpServerPopup && (
        <div style={{ display: "grid", justifyItems: "center" }}>
          <div style={{ backdropFilter: "blur(30px)", borderRadius: 12, border: "2px solid #e97730", top: "12%", position: "absolute", zIndex: 999999999, width: isMobile ? "85%" : "40%", height: "30rem", backgroundColor: "rgba(255,255,255,0.9)" }}>
            <div style={{ display: "grid", justifyItems: "center", padding: "2rem" }}>
              <button style={{ position: "absolute", backgroundColor: "transparent", border: "none", right: 10, top: 10 }} onClick={() => setShowJumpServerPopup(false)}>
                <FaX style={{ color: "#e97730", fontSize: 18 }} />
              </button>
              <h3 style={{ color: "#154e7a", fontWeight: 700 }}>Jump Server Details</h3>
              {loadingJumpServer ? <p style={{ marginTop: "2rem" }}>Loading...</p> : (
                <>
                  <div style={{ marginTop: "1.5rem", width: "100%", display: "grid", rowGap: 12, color: "#154e7a", fontWeight: 600 }}>
                    <p><b>CPU:</b> {jumpServerDetails?.cpu}</p><p><b>RAM:</b> {jumpServerDetails?.ram}</p>
                    <p><b>Disk:</b> {jumpServerDetails?.disk}</p><p><b>Price:</b> ${jumpServerDetails?.price} / Month</p>
                  </div>
                  <div style={{ marginTop: "2rem", display: "flex", gap: 15, justifyContent: "center" }}>
                    <button style={{ padding: "10px 25px", backgroundColor: "#e97730", color: "#fff", border: "none", borderRadius: 6, fontWeight: 600, cursor: "pointer" }} onClick={() => { setShowJumpServerPopup(false); setCounter(1); setShowIPOptionPopup(true); }}>Continue</button>
                    <button style={{ padding: "10px 25px", backgroundColor: "transparent", color: "#e97730", border: "2px solid #e97730", borderRadius: 6, fontWeight: 600, cursor: "pointer" }} onClick={() => setShowJumpServerPopup(false)}>Back</button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── MOBILE VIEW ───────────────────────────────────────────────────────── */}
      {isMobile ? (
              <>
                {/* MOBILE VIEW */}
                <div className="scrollable-container" style={{ marginTop: "2rem" }}>
                  {osList.map(({ label, icon, iconStyle, options, value, onChange }) => (
                    <OsSelector key={label} label={label} icon={icon} iconStyle={iconStyle} options={options} value={value} onChange={onChange} isMobile />
                  ))}
                </div>
      
                <div style={{ marginTop: "6rem", marginBottom: "75px" }}>
                  <div style={{ borderTop: "1px solid #919191", margin: 6 }} />
                  <div style={{ marginTop: 15, display: "flex", height: "3rem", justifyContent: "space-around", marginLeft: 10 }}>
                    {/* Time select */}
                    <select name="time" style={{ height: 35, width: 100, borderRadius: 30, padding: "5px", border: "2px solid #e97730" }} onChange={(e) => handleTimeChange(e.target.value)}>
                      <option value="1">1 Month</option>
                      <option value="3">3 Month</option>
                      <option value="6">6 Month</option>
                      <option value="9">9 Month</option>
                      <option value="12">1 Year</option>
                      <option value="24">2 Years</option>
                    </select>
                    {/* Location select */}
                    <select name="plan_time" style={{ marginLeft: 15, borderRadius: 30, border: "2px solid #e97730", width: "10rem", height: 35 }} value={selectedLocation}
                      onChange={(e) => setSelectedLocation(e.target.value)}>
                      {serverLocaiton?.map((item, i) => <option key={i} value={item.id}>{item.server_location}</option>)}
                    </select>
                    {/* Machine name */}
                    <div className="input-container" style={{ marginLeft: 5, height: 35, border: "2px solid #e97730", width: "10rem", marginTop: 0, marginRight: 15 }}>
                      <input value={newMachineName ?? ""} type="text" className="input-create-machine" placeholder="Name of Machine" style={{ width: "4rem", color: "black" }} onChange={(e) => setNewMachineName(e.target.value)} />
                    </div>
                  </div>
      
                  {/* Price display */}
                  <div style={{ display: "flex", alignItems: "center", marginLeft: 5, color: "white", fontWeight: 600 }}>
                    {renderPriceDisplay()}
                  </div>
      
      <div style={{ marginBottom: "1.5rem", maxWidth: 400 }}>
        
      </div>
                  {/* Selection badges */}
                  <div className="buttons-container" style={{ padding: 0 }}>
                    {[`${selectedCPU} vCPU`, `${selectedRAM} GB RAM`, `${selectedSSD} GB Disk`, `${selectedDT} TB Data Transfer`].map((label) => (
                      <Button key={label} className="mobile-top-buttons-creact-machine">{label}</Button>
                    ))}
                    <a onClick={handleLaunchClick} className="top-buttons-creact-machine" style={{ marginTop: 5, marginLeft: 10, justifyContent: "right", fontSize: 16, color: "white", fontWeight: 600 }}>
                      Create Machine
                    </a>
                  </div>
      
                  {/* Page tabs */}
                  <div>
                    {NATIVE_PAGES.map((item, i) => (
                      <div key={item} className={activePage === item ? "active-mobile-vm" : "non-active-mobile-vm"}
                        style={{ marginLeft: `${i * 90}px` }}
                        onClick={() => { setActivePage(item); setSelectedIdx(null); setFinalAmount("0"); setDiscountRate("0"); }}>
                        {item}
                      </div>
                    ))}
                  </div>
      
                  {/* Package cards / custom sliders */}
                  <div className="tab-box-mobile-create-machine">
                    {activePage === "Custom Configure" ? (
                      <>
                        <div className="buttons-container" style={{ padding: 15 }}>
                          {INNER_BUTTONS.map((title, idx) => (
                            <Button key={idx} style={{ background: activeButton === title ? "#f47c20" : "#035189", border: "none", fontSize: 11, padding: "5px 15px", color: "#fff", fontWeight: 600, borderRadius: 5, marginBottom: 10 }}
                              onClick={() => { setActiveButton(title); setConfigType(idx + 1); }}>
                              {title}
                            </Button>
                          ))}
                        </div>
                        <div className="title-box">
                          {["vCPU", "RAM"].map((h) => <h6 key={h} style={{ width: "25%", textAlign: "center", fontSize: 16, fontWeight: 700, marginTop: 10 }}>{h}</h6>)}
                          <div className="ssd price" data-value="40" style={{ textAlign: "center", fontWeight: 600, fontSize: 16, position: "relative" }}>
                            {renderCustomDiskSelect()}
                          </div>
                          <h6 style={{ width: "25%", textAlign: "center", fontSize: 16, fontWeight: 700 }}>BAND<br />WIDTH</h6>
                        </div>
                        {/* Vertical sliders */}
                        <div style={{ marginLeft: "-24rem", marginTop: "22rem", position: "absolute" }}>
                          {[
                            { val: customCPU, set: (v) => { setCustomCPU(v); setSelectedCPU(v); }, max: 32, label: customCPU, left: "1.9rem" },
                            { val: customRAM, set: (v) => { setCustomRAM(v); setSelectedRAM(v); }, max: 510, label: customRAM, left: "6.9rem" },
                            { val: customDISK, set: (v) => { setCustomDISK(v); setSelectedSSD(v); }, max: diskType === "hdd" ? 10000 : 5000, label: customDISK, left: "12rem" },
                            { val: customDATAT, set: (v) => { setCustomDATAT(v); setSelectedDT(v); }, max: 32, label: customDATAT, left: "17.9rem" },
                          ].map(({ val, set, max, label, left }, si) => (
                            <div key={si} className="range-slider-vrt">
                              <div className="tooltip-horz-vrt" style={{ left, top: "-13rem", position: "absolute" }}>{label}</div>
                              <input type="range" min="0" max={max} value={val}
                                onChange={(e) => set(Number(e.target.value))}
                                className="custom-rangeInput-vrt"
                                style={{ left: `${["-6rem", "-1rem", "4.2rem", "10rem"][si]}`, position: "absolute", background: `linear-gradient(to right, #e97730 ${(val * 100) / max}%, #ddd ${(val * 100) / max}%)` }} />
                            </div>
                          ))}
                        </div>
                      </>
                    ) : (
                      <div style={{ marginLeft: "-1rem" }}>
                        {currentVmList.map((item, idx) => (
                          <PackageCard key={idx} item={item} idx={idx} selectedIdx={selectedIdx} activePage={activePage} diskType={diskType} isMobile
                            smuser={smuser} appCurrency={appCurrency} newMachineTime={newMachineTime}
                            hoverIndex={hoverIndex} handleMouseEnter={setHoverIndex} handleMouseLeave={() => setHoverIndex(null)}
                            stocksForDisk={currentStocks} onChangeDisk={handleDiskChange} onSelect={handleCardSelect} />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </>
        )  : (
        /* ── WEB VIEW ─────────────────────────────────────────────────────── */
        <>
          <Row>
            <div className="col-md-1" />
            <div className="see-width col-md-11" style={{ marginTop: "4rem", paddingLeft: "2rem", marginBottom: "4rem" }}>
              <div style={{ display: "flex", overflowX: "auto", whiteSpace: "nowrap", padding: "10px 0", width: "100%", scrollbarWidth: "thin" }}>
                {osList.map(({ label, icon, iconStyle, options, value, onChange }) => (
                  <OsSelector key={label} label={label} icon={icon} iconStyle={iconStyle} options={options} value={value} onChange={onChange} isMobile={false} />
                ))}
              </div>
              <hr />
              <div className="buttons-container" style={{ marginTop: "2rem", display: "flex", flexWrap: "wrap", marginBottom: "2rem" }}>
                {[`${selectedCPU} vCPU`, `${selectedRAM} GB RAM`, `${selectedSSD} GB Disk`, `${selectedDT} TB Bandwidth`].map((label) => (
                  <Button key={label} className="top-buttons-creact-machine">{label}</Button>
                ))}
                <select name="plan_time" style={{ borderRadius: 30, marginRight: 10, padding: "10px 15px", border: "2px solid #e97730" }} onChange={(e) => handleTimeChange(e.target.value)}>
                  <option value="1">1 Month</option><option value="3">3 Month</option><option value="6">6 Month</option>
                  <option value="9">9 Month</option><option value="12">1 Year</option><option value="24">2 Years</option>
                </select>
                <select name="plan_time" style={{ borderRadius: 30, marginRight: 10, padding: "10px 15px", border: "2px solid #e97730", width: "9rem" }}
                  value={selectedLocation} onChange={(e) => { setSelectedLocation(e.target.value); onChangeLocation(e.target.value); }}>
                  <option value="Select">Select Location</option>
                  {serverLocaiton?.map((item, i) => <option key={i} value={item.id}>{item.server_location}</option>)}
                </select>
                <div className="input-container" style={{ border: "2px solid #e97730", width: "20rem", marginTop: 0 }}>
                  <input type="text" className="input-create-machine" placeholder="Name of Machine" value={newMachineName ?? ""} style={{ color: "black", textAlign: "center", width: "15px" }} onChange={(e) => setNewMachineName(e.target.value)} />
                </div>
                <a onClick={handleLaunchClick} className="top-buttons-creact-machine" style={{ marginLeft: 10, padding: "15px", borderRadius: 15 }}>Create Machine</a>
                {renderPriceDisplay()}
                  
              </div>
              <div className="tab-box-web-vm" style={{ height: "60vh !important" }}>
                {NATIVE_PAGES.map((item) => (
                  <div key={item} className={activePage === item ? "active-web-vm" : "non-active-web-vm"}
                    onClick={() => { setActivePage(item); setSelectedIdx(null); setFinalAmount("0"); }}
                    style={item === "Standard" ? { paddingTop: activePage === "Standard" ? "44px" : "33px" } : {}}>
                    {item}
                  </div>
                ))}
                <div style={{ marginLeft: "1rem", marginTop: "-29rem" }}>
                  {activePage === "Custom Configure" ? (
                    <>
                      <div className="buttons-container" style={{ padding: 15, marginTop: "-8rem" }}>
                        {INNER_BUTTONS.map((title, idx) => (
                          <Button key={idx} style={{ background: activeButton === title ? "#f47c20" : "#035189", border: "none", fontSize: 22, padding: "5px 15px", color: "#fff", fontWeight: 600, borderRadius: 5, marginBottom: 10 }}
                            onClick={() => { setActiveButton(title); setConfigType(idx + 1); setDisktype("ssd"); }}>
                            {title}
                          </Button>
                        ))}
                      </div>
                      <div className="title-box-slider" style={{ backgroundImage: `url("/images/frontend/price/server-img.png")`, backgroundPosition: "center" }}>
                        {["vCPU", "RAM"].map((h) => <h6 key={h} style={{ textAlign: "center", fontSize: 30, fontWeight: 800, marginTop: h === "vCPU" ? 25 : 60 }}>{h}</h6>)}
                        <h6 style={{ textAlign: "center", fontSize: 30, fontWeight: 800, marginTop: 55 }}>
                          <div className="ssd price" style={{ textAlign: "center", fontWeight: 600, fontSize: 24, position: "relative" }}>{renderCustomDiskSelect()}</div>
                        </h6>
                        <h6 style={{ textAlign: "center", fontSize: 30, fontWeight: 800, marginTop: 60 }}>BANDWIDTH</h6>
                      </div>
                      <div style={{ display: "flex", marginLeft: "22rem", position: "absolute", marginTop: "-26rem", flexDirection: "column", width: "60%" }}>
                        {[
                          { val: customCPU,   set: (v) => { setCustomCPU(v);   setSelectedCPU(v); }, max: maxVcpu,      pct: pctVcpu,      label: `${pctVcpu === 0 ? 0 : customCPU} vCPU` },
                          { val: customRAM,   set: (v) => { setCustomRAM(v);   setSelectedRAM(v); }, max: maxRam,       pct: pctRam,       label: pctRam === 0 ? "0 GB" : `${customRAM} GB` },
                          { val: customDISK,  set: (v) => { setCustomDISK(v);  setSelectedSSD(v); }, max: maxDisk,      pct: pctDisk,      label: pctDisk === 0 ? "0 GB" : `${customDISK} GB` },
                          { val: customDATAT, set: (v) => { setCustomDATAT(v); setSelectedDT(v);  }, max: maxBandwidth, pct: pctBandwidth, label: `${pctBandwidth === 0 ? 0 : customDATAT} TB` },
                        ].map(({ val, set, max, pct, label }, si) => (
                          <div key={si}>
                            <div className="range-slider">
                              <div className="tooltip-horz" style={{ left: `${pct}%` }}>{label}</div>
                              <input type="range" min="0" max={max} value={pct === 0 ? 0 : val}
                                onChange={(e) => set(Number(e.target.value))}
                                className="custom-rangeInput"
                                style={{ background: `linear-gradient(to right, #e97730 ${pct}%, #ddd ${pct}%)` }} />
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="standard-machine-list" style={{ height: "60vh !important" }}>
                      {currentVmList.map((item, idx) => (
                        <PackageCard key={idx} item={item} idx={idx} selectedIdx={selectedIdx} activePage={activePage} diskType={diskType} isMobile={false}
                          smuser={smuser} appCurrency={appCurrency} newMachineTime={newMachineTime}
                          hoverIndex={hoverIndex} handleMouseEnter={setHoverIndex} handleMouseLeave={() => setHoverIndex(null)}
                          stocksByType={stocksByType} onChangeDisk={handleDiskChange} onSelect={handleCardSelect} />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Row>
        </>
      )}

      {loading && <div className="loading-overlay" style={{ zIndex: 9999999999 }}><Loader isLoading={loading} /></div>}
    </div>
  );
};

export default CreateMachine;